import type { LeaveRequest } from "@spork/s4p-server-sdk";

import { toDateOnlyIso } from "@/lib/date";

export type LeaveRequestStatusStyles = {
    containerClassName: string;
    textClassName: string;
};

export function getLeaveRequestStatusStyles(
    status: LeaveRequest["status"],
): LeaveRequestStatusStyles {
  switch (status) {
    case "pending":
      return {
        containerClassName: "bg-attendance-pending",
        textClassName: "text-primary-foreground",
      };
    case "needs_info":
      return {
        containerClassName: "bg-warning",
        textClassName: "text-warning-foreground",
      };
    case "approved":
      return {
        containerClassName: "bg-success",
        textClassName: "text-success-foreground",
      };
    case "conditionally_approved":
      return {
        containerClassName: "bg-attendance-pending",
        textClassName: "text-primary-foreground",
      };
    case "rejected":
      return {
        containerClassName: "bg-error",
        textClassName: "text-error-foreground",
      };
    case "cancelled":
      return {
        containerClassName: "bg-muted",
        textClassName: "text-muted-foreground",
      };
    default:
      return {
        containerClassName: "bg-muted",
        textClassName: "text-muted-foreground",
      };
  }
}

/**
 * True when the request should appear in the default parent list
 * (open workflow or approved/conditionally approved and not yet ended).
 */
export function isActiveLeaveRequest(request: LeaveRequest, todayIso: string): boolean {
  if (request.status === "pending" || request.status === "needs_info") {
    return true;
  }

  if (request.status === "approved" || request.status === "conditionally_approved") {
    if (request.date_to == null) {
      return true;
    }

    // Compare calendar days only — date_to is a DATE, not a timestamp.
    return toDateOnlyIso(request.date_to) >= toDateOnlyIso(todayIso);
  }

    return false;
}

/** Parent may cancel pending, needs_info, approved, or conditionally approved requests. */
export function canParentCancelLeaveRequest(status: LeaveRequest["status"]): boolean {
  return (
    isOpenLeaveRequest(status)
    || status === "approved"
    || status === "conditionally_approved"
  );
}

/** Parent may add/remove attachments while the request is still editable. */
export function canParentManageLeaveRequestAttachments(
  request: Pick<LeaveRequest, "status" | "events">,
): boolean {
  if (isOpenLeaveRequest(request.status)) {
    return true;
  }

  // Auto-approved creates land in `approved` before the client can upload files.
  // Trust the server timeline instead of re-implementing auto-approve date rules.
  return (
    request.status === "approved"
    && (request.events ?? []).some((event) => event.type === "auto_approved")
  );
}

/** Parent may resubmit after the school requested additional information. */
export function canParentResubmitLeaveRequest(status: LeaveRequest["status"]): boolean {
  return status === "needs_info";
}

/**
 * Parent may set or change date_to on an illness request that is approved
 * or still pending school review.
 */
export function canParentUpdateLeaveRequestEndDate(
  status: LeaveRequest["status"],
  reason: LeaveRequest["reason"],
): boolean {
  return (
    reason === "illness"
    && (status === "approved" || status === "pending")
  );
}

/**
 * True when the request is still open in the workflow and needs parent or school attention.
 */
export function isOpenLeaveRequest(status: LeaveRequest["status"]): boolean {
  return status === "pending" || status === "needs_info";
}

/** Requests that need parent attention first (needs_info), then awaiting school. */
export function getLeaveRequestPriority(status: LeaveRequest["status"]): number {
  switch (status) {
    case "needs_info":
      return 0;
    case "pending":
      return 1;
    case "approved":
      return 2;
    case "conditionally_approved":
      return 3;
    case "rejected":
      return 4;
    case "cancelled":
      return 5;
    default:
      return 6;
  }
}
